import {
    EzRegistrationState
} from '/ezlibrary/enums/EzEnumerations.js';

import { EzClass } from '/ezlibrary/EzClass.js';
import { EzEventEngine } from '/ezlibrary/ez-event-engine.js';

/**
 * @class
 * @extends {EzClass}
 * @description
 * Obtains information about the local browser.
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *  import { EzLocalBrowserInfo } from '/ezlibrary/analytics_metrics/EzLocalBrowserInfo.js';
 *~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * window?.ezApi?.ezclocker?.[EzLocalBrowserInfo.ezApiName]?.ready
 *~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 *  EzClass.ezWaitReady(
 *     EzLocalBrowserInfo.ezEventNames.ezOnReady,
 *     {listenting_class}.#ezRegistrator);
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzLocalBrowserInfo extends EzClass {
    ezBrowserInfo = '';

    ezIpAddresses = {};

    ezInit() {
        EzLocalBrowserInfo.ezInstance.ezGatherLocalBrowserInfo();
        return EzLocalBrowserInfo.ezInstance;
    }

    ezGatherLocalBrowserInfo() {
        EzLocalBrowserInfo.ezInstance.ezObtainIpFromRTCPeerConnection();
    }

    ezObtainIpFromRTCPeerConnection() {
        // NOTE: window.RTCPeerConnection is "not a constructor" in FF22/23
        const RTCPeerConnection = /*window.RTCPeerConnection ||*/ window?.webkitRTCPeerConnection || window?.mozRTCPeerConnection;

        if (!RTCPeerConnection) {
            EzLocalBrowserInfo.ezInstance.ezBrowserInfo = globalThis.ezApi.ezTemplate`${EzLocalBrowserInfo.ezInstance}
                <div>Unable to obtain ip from RTCPeerConnection. RTCPeerConnection is not available for browser.</div>`;

            globalThis.ezApi.ezclocker.logger.debug(
                globalThis.ezApi.ezMsg`
                    Unable to obtain ip from RTCPeerConnection. RTCPeerConnection is not available for browser.
                    <code>ifconfig | grep inet | grep -v inet6 | cut -d" " -f2 | tail -n1</code>
                    <dev>In Chrome and Firefox your IP should display automatically, by the power of WebRTCskull.</dev>`);
            return;
        }

        const rtc = new RTCPeerConnection(
            {
                iceServers: []
            }
        );

        const featureCheck = 1;

        if (featureCheck || window?.mozRTCPeerConnection) {
            // FF [and now Chrome!] needs a channel/stream to proceed
            rtc.createDataChannel(
                '',
                {
                    reliable: false
                });
        }

        rtc.onicecandidate = (event) => {
            // convert the candidate to SDP so we can run it through our general parser
            // see https://twitter.com/lancestout/status/525796175425720320 for details
            if (event.candidate) {
                EzLocalBrowserInfo.ezInstance.ezGrepSDP(`a=${event.candidate.candidate}`);
            }
        };

        rtc.createOffer().then(
            (offerDesc) => {
                EzLocalBrowserInfo.ezInstance.ezGrepSDP(offerDesc.sdp);
                rtc.setLocalDescription(offerDesc);
            },
            (error) => {
                EzLocalBrowserInfo.ezInstance.ezBrowserInfo = globalThis.ezApi.ezTemplate`${EzLocalBrowserInfo.ezInstance}
                    <div>
                        RTCPeerConnection offer to obtain ip address failed.
                        <div>Error: ${error.message}</div>
                    </div>`;
                globalThis.ezApi.ezclocker.ezLogger.warn(
                    `RTCPeerConnection offer to obtain ip address failed. Error: ${error.message}`);
            });

        EzLocalBrowserInfo.ezInstance.ezIpAddresses['0.0.0.0'] = false;
    }

    /**
     * @public @method
     *
     * @param {string} ipAddress
     */
    ezDisplayIpAddress(ipAddress) {
        if (ipAddress in EzLocalBrowserInfo.ezInstance.ezIpAddresses) {
            return;
        }

        EzLocalBrowserInfo.ezInstance.ezIpAddresses[ipAddress] = true;

        const displayAddrs = Object.keys(EzLocalBrowserInfo.ezInstance.ezIpAddresses)
            .filter(
                (key) => EzLocalBrowserInfo.ezInstance.ezIpAddresses[ipAddress][key]);

        EzLocalBrowserInfo.ezInstance.ezBrowserInfo = globalThis.ezApi.ezTemplate`
            ${EzLocalBrowserInfo.ezInstance.ezBrowserInfo}
            ${displayAddrs.join(' or perhaps ') || 'n/a'}`;

        globalThis.ezApi.ezclocker.ezLogger.debug(
            globalThis.ezApi.ezMsg`
                Local browser information: ${EzLocalBrowserInfo.ezInstance.ezBrowserInfo}`);
    }

    /**
     * @public @method
     *
     * @param {object} sdp
     */
    ezGrepSDP(sdp) {
        sdp.split('\r\n').forEach(
            (line) => {
                if (~line.indexOf('a=candidate')) {
                    const parts = line.split(' ');

                    const addr = parts[4];

                    const type = parts[7];

                    if (type === 'host') {
                        EzLocalBrowserInfo.ezInstance.ezDisplayIpAddress(addr);
                    }
                } else if (~line.indexOf('c=')) {
                    const parts = line.split(' ');

                    const addr = parts[2];

                    EzLocalBrowserInfo.ezInstance.ezDisplayIpAddress(addr);
                }
            });
    }

    /**
     * @public @method
     * Obtain the local user IP4 address.
     */
    getPublicIP4() {
        globalThis.ezApi.ezclocker.ezHttpHelper.ezGet(
            'https://api.ipify.org?format=json')
            .then(
                (response) => {
                    if (globalThis.ezApi.ezIsValid(response) && globalThis.ezApi.ezHasOwnProperty(response, 'ip')) {
                        EzLocalBrowserInfo.ezInstance.ezLocalIP4 = response.ip;
                    } else {
                        globalThis.ezApi.ezclocker.logger.error(
                            globalThis.ezApi.ezEM`
                                Unable to obtained the local IP4 from IPIFY.
                                Response: ${globalThis.ezApi.ezToJson(response)}`);
                        EzLocalBrowserInfo.ezInstance.ezLocalIP4 = 'UNKNOWN';
                    }
                },
                (eResponse) => {
                    globalThis.ezApi.ezclocker.logger.error(
                        globalThis.ezApi.ezEM`
                            Unable to obtained the local IP4 from IPIFY.
                            Response: ${globalThis.ezApi.ezToJson(eResponse)}`);
                });
    }

    /**
     * @public @method
     * Optain the local user IP6 address
     */
    getPublicIP6() {
        globalThis.ezApi.ezclocker.ezHttpHelper.ezGet(
            'https://api64.ipify.org?format=json')
            .then(
                (response) => {
                    if (globalThis.ezApi.ezIsValid(response) && globalThis.ezApi.ezHasOwnProperty(response, 'ip')) {
                        EzLocalBrowserInfo.ezInstance.ezLocalIP4 = response.ip;
                    } else {
                        globalThis.ezApi.ezclocker.logger.error(
                            globalThis.ezApi.ezEM`
                                Unable to obtained the local IP6 from ipify.org api.
                                Response: ${globalThis.ezApi.ezToJson(response)}`);
                        EzLocalBrowserInfo.ezInstance.ezLocalIP4 = 'UNKNOWN';
                    }
                },
                (eResponse) => {
                    globalThis.ezApi.ezclocker.logger.error(
                        globalThis.ezApi.ezEM`
                            Unable to obtained the local IP4 from ipify.org api.
                            Response: ${globalThis.ezApi.ezToJson(eResponse)}`);
                });
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Class Initialization and EzApi Registration Section
    // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond this section !!
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

    /**
     * @static
     * @public @readonly @property
     * Gets the name of this class's singleton instance when registered with globalThis.ezApi.
     *
     * @returns {string}
     */
    static get ezApiName() {
        return 'ezLocalBrowserInfo';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets an object of event names that this class may trigger.
     *
     * @returns {object}
     */
    static get ezEventNames() {
        return {
            onReady: 'ezOn_EzLocalBrowserInfo_Ready'
        };
    }

    /**
     * @static
     * @private @field
     * Stores the singleton instance of this class that was created by and registerd with globalThis.ezApi.
     *
     * @type {EzLocalBrowserInfo}
     */
    static #ezInstance = window?.ezApi?.ready && window?.ezApi?.ezclocker?.[EzLocalBrowserInfo.ezApiName]
        ? globalThis.ezApi.ezclocker[EzLocalBrowserInfo.ezApiName]
        : null;
    /**
     * @static
     * @public @property @getter
     * Gets the singleton instance of this class that is registered with EzApi (if available yet)
     *
     * @returns {EzLocalBrowserInfo}
     */
    static get ezInstance() {
        return EzLocalBrowserInfo.#ezInstance;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the singleton instance of of this class that is registered with globalThis.ezApi.
     *
     * @param {EzLocalBrowserInfo} instance
     */
    static set ezInstance(instance) {
        if (null != EzLocalBrowserInfo.#ezInstance) {
            throw new Error('EzLocalBrowserInfo\'s singleton instance is already reigstered with globalThis.ezApi.');
        }

        EzLocalBrowserInfo.#ezInstance = instance;
    }

    /**
     * @static
     * @private @field
     * Stores the EzApi registration state for this class.
     * Default value is NULL
     * Class is registerd with EzApi when value is EzRegistrationState.REGISTERED
     *
     * @type {string}
     * A valid enum property value from EzRegistrationState
     */
    static #ezApiRegistrationState = window?.ezApi?.ready && window.ezApi?.ezclocker?.[EzLocalBrowserInfo.ezApiName]
        ? EzRegistrationState.REGISTERED
        : null;
    /**
     * @static
     * @public @property @getter
     * Gets the ezApi registration state of this classes's singleton instance.
     *
     * @returns {string}
     * A valid enum property value from EzRegistrationState
     */
    static get ezApiRegistrationState() {
        return EzLocalBrowserInfo.#ezApiRegistrationState;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the ezApi registration state of this classes's singleton instance.
     *
     * @param {string} ezApiRegistrationState
     * A valid enum property value from EzRegistrationState
     */
    static set ezApiRegistrationState(ezApiRegistrationState) {
        EzLocalBrowserInfo.#ezApiRegistrationState = EzRegistrationState.ezValueOf(ezApiRegistrationState);
    }

    /**
     * @static
     * @private @readonly @property
     * Gets true when all required dependencies for this class report ready.
     * In otherwords, the require dependency's singleton instance is created
     * and is registered with and accessable from EzApi with globalThis.ezApi.ezclocker.{dependency_name}.
     *
     * @returns {boolean}
     */
    static get #ezCanRegister() {
        return EzRegistrationState.PENDING === EzLocalBrowserInfo.ezApiRegistrationState &&
            window?.ezApi?.ready &&
            window?.ezApi?.ezclocker?.[EzEventEngine.ezApiName]?.ready;
    }

    /**
     * @static
     * @private @readonly @property
     * Gets if this class's singleton instance is registered with ezApi yet.
     *
     * @returns {boolean}
     */
    static get #ezIsRegistered() {
        return null != EzLocalBrowserInfo.ezInstance &&
            EzRegistrationState.REGISTERED === EzLocalBrowserInfo.ezApiRegistrationState;
    }

    /**
     * @static
     * @private @method
     * Attempts to register the singleton instance for this class with globalThis.ezApi. Returns true
     * if successful, false otherwise.
     *
     * @returns {boolean}
     */
    static #ezRegistrator() {
        if (EzLocalBrowserInfo.#ezCanRegister && !EzLocalBrowserInfo.#ezIsRegistered) {
            globalThis.ezApi.ezRegisterNewApi(EzLocalBrowserInfo, EzLocalBrowserInfo.ezApiName);
        }

        return EzLocalBrowserInfo.#ezIsRegistered;
    }

    /**
     * @static
     * Static Initialization
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * >> IMPORTANT <<
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Make sure the following properties and methods get defined BEFORE this static initialization section:
     *     1) Property getter EzLocalBrowserInfo.ezApiName
     *     2) Property getter EzLocalBrowserInfo.ezEventNames
     *     3) Property getter EzLocalBrowserInfo.ezInstance
     *     4) Property setter EzLocalBrowserInfo.ezInstance
     *     5) Property getter EzLocalBrowserInfo.ezApiRegistrationState
     *     6) Property setter EzLocalBrowserInfo.ezApiRegistrationState
     *     7) Property getter EzLocalBrowserInfo.#ezCanRegister
     *     8) Property getter EzLocalBrowserInfo.#ezIsRegistered
     *     9) Method EzLocalBrowserInfo.#ezRegistrator()
     */
    static {
        if (!EzLocalBrowserInfo.#ezIsRegistered) {
            EzLocalBrowserInfo.ezApiRegistrationState = EzRegistrationState.PENDING;

            if (!EzLocalBrowserInfo.#ezRegistrator()) {
                EzClass.ezWaitReady(
                    EzClass.ezOnEzApiReadyEventName,
                    EzLocalBrowserInfo.#ezRegistrator);
            }
        }
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond above section !!
    // NO CODE AFTER THIS SECTION
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

}

globalThis.EzLocalBrowserInfo = EzLocalBrowserInfo;
